import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { mapArrayToObject } from "../../commonFunctions/arrayFunc";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import Button from "../../components/FormComponents/Button";
import Input from "../../components/FormComponents/Input";
import Label from "../../components/FormComponents/Label";
import { SCREEN_WIDTHS } from "../../constant/constant";
import { useTemplateContext } from "../../context/TemplateContext";
import useZTheme from "../../hooks/useZTheme";
import BuilderTabs from "../../Layouts/BuilderTabs";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import PageInfoAndActionBar from "../../Layouts/PageInfoAndActionBar";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { elementConfig } from "../../utils/Config/DefaultConfig/elementConfig";
import { getFieldIcon } from "../../utils/getFieldIcon";
import { getSidebarElementsForPositionForm } from "../../utils/sidebarElementList";
import { toastSuccess } from "../../utils/toastUtils";
import { getToolTipAttributes } from "../../utils/tooltip";
import { RvElementBar } from "../ApplicationBlock/RecordsViewRenderer/Desktop/PositionedDesktopRecordview";
import PositionScreenWithEdit from "../FlexibleLayout/PositionScreenWithEdit";
import { getElementIds, getOffset, getPositionedElementConfig } from "../FlexibleLayout/utils";
import FormFieldsProvider from "./FormFieldsProvider";

// const initialLayoutState = {
//   showGrid: true,
//   showTree: false,
//   showTemplate: false,
//   screenMode: "editable",
//   scaleFactor: 1,
//   gridSize: 4
// };
const initialModalState = {
  showModal: false,
  modalType: "add",
  modalData: {},
  modalTitle: ""
};

const getScreenWidth = (positionFormData, device) => {
  const positionedData = positionFormData?.config?.positioned_data;
  const deviceData = positionedData?.[device];
  const size = deviceData?.layout_size;

  if (size) return size;

  if (device === "desktop") return 750;

  return SCREEN_WIDTHS[device];
};

const PositionFormPlayground = () => {
  // const [screenLayoutInfo, setScreenLayoutInfo] = useState(initialLayoutState);
  const [modalState, setModalState] = useState(initialModalState);

  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [positionFormData, setPositionFormData] = useState({});
  const [activityData, setActivityData] = useState({});
  const [selectedDevice, setSelectedDevice] = useState("desktop");
  useZTheme(selectedDevice);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { formId, appId, orgId, smId } = useParams();
  const navigate = useNavigate();
  const [insideElementIds, setInsideElementIds] = useState(new Set());

  const openModal = (type, title, data) => {
    setModalState({
      showModal: true,
      modalType: type,
      modalData: data,
      modalTitle: title
    });
  };
  const closeModal = () => {
    setModalState(initialModalState);
  };

  const fetchActivityAndScreenData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [activityRes, formRes] = await Promise.all([
        axiosInstance.get(`/state-machine/${smId}/activities/${formId}`),
        axiosInstance.get(`/positioned-form/sm/${smId}/activity/${formId}`)
      ]);

      setActivityData(activityRes.data);
      setPositionFormData(formRes.data);
    } catch (err) {
      console.error("Error fetching activity or form data:", err);
      handleApiError(err);
      setError(err?.message || "Failed to load screen data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivityAndScreenData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSavePositionForm = async ({ idsForDevice }) => {
    try {
      // update position ids for the all device  positionFormData.config.positioned_data.desktop.field_ids
      const copyPositionFormData = {
        ...positionFormData,
        config: {
          ...positionFormData.config,
          positioned_data: {
            ...positionFormData.config.positioned_data,
            desktop: {
              ...positionFormData.config.positioned_data.desktop,
              field_ids: idsForDevice.desktop
            },
            tablet: {
              ...positionFormData.config.positioned_data.tablet,
              field_ids: idsForDevice.tablet
            },
            mobile: {
              ...positionFormData.config.positioned_data.mobile,
              field_ids: idsForDevice.mobile
            }
          }
        }
      };
      const { message } = await axiosInstance.patch(
        `/positioned-form/sm/${smId}/activity/${formId}`,
        copyPositionFormData
      );
      return { message };
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleModalSave = async () => {
    const res = await handleSavePositionForm({ idsForDevice: modalState.modalData.idsForDevice });
    closeModal();
    toastSuccess(res?.message || "Position Form Saved Successfully");
  };

  const savePositionForm = async () => {
    try {
      const idsForDevice = {
        desktop: getElementIds(positionFormData?.config?.positioned_data?.desktop?.layout_data || []),
        tablet: getElementIds(positionFormData?.config?.positioned_data?.tablet?.layout_data || []),
        mobile: getElementIds(positionFormData?.config?.positioned_data?.mobile?.layout_data || [])
      };
      const currentFields = activityData.form.sections[0].fields;
      const missingFields = {
        desktop: currentFields.filter((field) => !idsForDevice.desktop["form-field"]?.includes(field.id)),
        tablet: currentFields.filter((field) => !idsForDevice.tablet["form-field"]?.includes(field.id)),
        mobile: currentFields.filter((field) => !idsForDevice.mobile["form-field"]?.includes(field.id))
      };
      // if any device has missing field than open modal
      if (Object.values(missingFields).some((fields) => fields.length > 0)) {
        openModal("alert", "Missing Fields", { missingFields, idsForDevice });
        return;
      }

      return await handleSavePositionForm({ idsForDevice });
    } catch (error) {
      handleApiError(error);
    }
  };

  const backToFormPage = () => {
    navigate(`/organization/${orgId}/app/${appId}/smform/${smId}/form/${formId}`);
  };
  const handleLayoutConfig = (newLayoutData) => {
    setPositionFormData((prev) => {
      const positionedData = prev?.config?.positioned_data || {};
      const deviceConfig = positionedData[selectedDevice] || {};
      const currentLayout = deviceConfig.layout_data || [];

      const updatedLayout = typeof newLayoutData === "function" ? newLayoutData(currentLayout) : newLayoutData;

      return {
        ...prev,
        config: {
          ...prev.config,
          positioned_data: {
            ...positionedData,
            [selectedDevice]: {
              ...deviceConfig,
              layout_data: updatedLayout
            }
          }
        }
      };
    });
  };

  const handleDragStart = (e, item) => {
    const { type, id } = item;
    let newConfig = getPositionedElementConfig(type, selectedDevice);
    if (newConfig.type === "element") {
      newConfig.config = elementConfig[type];
      if (newConfig.config?.type === "custom-component") {
        newConfig.config = { ...newConfig.config, id };
      }
    }
    if (item.type === "form-field") {
      const { field } = item;
      newConfig.config = {
        type,
        field: {
          type: field.type,
          id: field.id,
          label: field.label
        }
      };
    }
    const offset = getOffset(e);
    setDragOffset(offset);
    setDraggedItem(newConfig);
  };

  const handleChangeWidth = (width) => {
    //   update layout size
    setPositionFormData({
      ...positionFormData,
      config: {
        ...positionFormData.config,
        positioned_data: {
          ...positionFormData.config.positioned_data,
          [selectedDevice]: {
            ...positionFormData.config.positioned_data[selectedDevice],
            layout_size: width
          }
        }
      }
    });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Something went wrong</div>;
  }
  return (
    <div className="content_main_section">
      <div className="form_outer_body">
        <div className="form_outer_section">
          <div className="screen-center-wrapper">
            <PageInfoAndActionBar
              // pagePath={formsExplorerData ? getFilePathForForm(selectedForm, formsExplorerData) : ""}
              savePage={savePositionForm}
            />

            <div className="p_3 bg_gray d_flex justify_between position_relative">
              <div
                className="d_flex align_center gap_2 font_weight_semibold hover_border_bottom cursor_pointer"
                onClick={backToFormPage}
              >
                <MaterialIcon name="keyboard_backspace" className="font_weight_medium font_size_medium" />
                <span>Back to Form</span>
              </div>
              <DeviceSelector selectedDevice={selectedDevice} onSelectDevice={setSelectedDevice} />
              {selectedDevice === "desktop" && (
                <div className={"d_flex align_center gap_2"}>
                  <Label>Size : </Label>
                  <div className="d_flex align_center gap_2">
                    <Input
                      type="radio"
                      name="layout_size"
                      value="750"
                      label={"Default"}
                      checked={getScreenWidth(positionFormData, selectedDevice) === 750}
                      onChange={() => handleChangeWidth(750)}
                    />
                    <Input
                      type="radio"
                      name="layout_size"
                      value="1600"
                      label={"Large"}
                      checked={getScreenWidth(positionFormData, selectedDevice) === 1600}
                      onChange={() => handleChangeWidth(1600)}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="position-screen-center-page">
              <FromFieldsWrapperContext
                dragOffset={dragOffset}
                setDragOffset={setDragOffset}
                draggedItem={draggedItem}
                setDraggedItem={setDraggedItem}
                // screenLayoutInfo={screenLayoutInfo}
                layoutConfig={positionFormData?.config?.positioned_data?.[selectedDevice]?.layout_data || []}
                handleChangeConfig={handleLayoutConfig}
                // setScreenLayoutInfo={setScreenLayoutInfo}
                device={selectedDevice}
                tableId={smId}
                formFields={activityData.form.sections[0].fields}
                setInsideElementIds={setInsideElementIds}
                insideElementIds={insideElementIds}
                screenWidth={getScreenWidth(positionFormData, selectedDevice)}
              />
              {/* <PositionScreenActionBar
                screenLayoutInfo={screenLayoutInfo}
                setScreenLayoutInfo={setScreenLayoutInfo}
                device={selectedDevice}
              /> */}
            </div>
          </div>
          <PositionFormRightSidebar
            fields={activityData.form.sections[0].fields}
            insideElementIds={insideElementIds}
            handleDragStart={handleDragStart}
          />
        </div>
      </div>
      <BuilderModal modalName={modalState.modalTitle} isOpen={modalState.showModal} onClose={closeModal}>
        <AlertForTheMissingFields missingFields={modalState.modalData.missingFields} onSave={handleModalSave} />
      </BuilderModal>
    </div>
  );
};

const FromFieldsWrapperContext = ({
  dragOffset,
  setDragOffset,
  draggedItem,
  setDraggedItem,
  // screenLayoutInfo,
  layoutConfig,
  handleChangeConfig,
  // setScreenLayoutInfo,
  device,
  tableId,
  formFields,
  setInsideElementIds,
  screenWidth
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const handleElementInside = (id, action = "add") => {
    setInsideElementIds((prev) => {
      const newSet = new Set(prev);
      if (action === "add") {
        newSet.add(id);
      } else if (action === "delete") {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const { result } = mapArrayToObject(formFields, "id");

  return (
    <FormFieldsProvider value={{ formFieldMap: result, onElementInside: handleElementInside }}>
      <PositionScreenWithEdit
        layoutConfig={layoutConfig}
        setLayoutConfig={handleChangeConfig}
        draggedItem={draggedItem}
        setDragOffset={setDragOffset}
        setDraggedItem={setDraggedItem}
        dragOffset={dragOffset}
        // type={screenLayoutInfo.showTemplate ? "view" : screenLayoutInfo.screenMode}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        // gridSize={screenLayoutInfo.gridSize}
        screenWidth={screenWidth}
        // showTree={screenLayoutInfo.showTree}
        // setShowTree={() => setScreenLayoutInfo({ ...screenLayoutInfo, showTree: false })}
        // showGrid={screenLayoutInfo.showGrid}
        tableId={tableId}
        tableType={"statemachine"}
        device={device}
        // scaleFactor={screenLayoutInfo.scaleFactor}
      />
    </FormFieldsProvider>
  );
};

const PositionFormRightSidebar = ({ fields, handleDragStart, insideElementIds }) => {
  const { getReactComponents, reactComponent } = useTemplateContext();

  useEffect(() => {
    getReactComponents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="rightsidebar">
      <BuilderTabs
        tabs={[
          {
            label: "Fields",
            content: <FieldsList fields={fields} onDragStart={handleDragStart} insideElementIds={insideElementIds} />
          },
          {
            label: "Other",
            content: (
              <RvElementBar data={getSidebarElementsForPositionForm(reactComponent)} onDragStart={handleDragStart} />
            )
          }
        ]}
        contentClass="p_3"
        containerClass="form_field"
      />
    </div>
  );
};

const FieldsList = ({ fields, onDragStart, insideElementIds }) => {
  const [searchText, setSearchText] = useState("");
  const [showOnlyDraggable, setShowOnlyDraggable] = useState(false);

  const filteredFields = fields
    .filter((field) => field.label.toLowerCase().includes(searchText.toLowerCase()))
    .filter((field) => (showOnlyDraggable ? !insideElementIds.has(field.id) : true));

  return (
    <>
      <div className="d_flex align_center justify_between gap_2 mb_3 position_sticky bg_gray top_0">
        <Input
          type="text"
          inputClassName="p_2 border_radius_small border shadow_sm w_100"
          placeholder="Search fields..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Input
          type="checkbox"
          checked={showOnlyDraggable}
          onChange={(e) => setShowOnlyDraggable(e.target.checked)}
          {...getToolTipAttributes("Show only Non Selected fields")}
        />
      </div>
      <div style={{ maxHeight: "calc(100% - 116px)", overflowY: "auto", position: "absolute", width: "210px" }}>
        {/* Fields List */}
        {filteredFields.length === 0 ? (
          <div className="text_muted italic">No matching fields found.</div>
        ) : (
          filteredFields.map((item) => {
            const isInside = insideElementIds.has(item.id);
            return <FieldItem item={item} isInside={isInside} onDragStart={onDragStart} key={item.id} />;
          })
        )}
      </div>
    </>
  );
};

const FieldItem = ({ item, isInside, onDragStart }) => {
  const labelRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = labelRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, [item.label]);
  const tooltipProps = isOverflowing ? getToolTipAttributes(item.label) : {};
  return (
    <div
      key={item.id}
      className={`d_flex align_center shadow mb_2 gap_2 p_2 bg_light border_radius_small w_100 ${
        isInside ? "cursor_default opacity_50" : "cursor_move"
      }`}
      onDragStart={(e) => {
        if (!isInside) {
          onDragStart(e, { field: item, type: "form-field" });
        } else {
          e.preventDefault();
        }
      }}
      draggable={!isInside}
      {...tooltipProps}
    >
      <MaterialIcon name={getFieldIcon(item.type)} />
      <div className="text_overflow overflow_hidden no_wrap" style={{ maxWidth: "100%" }} ref={labelRef}>
        {item.label}
      </div>
    </div>
  );
};

const devices = [
  { id: "desktop", label: "Desktop", icon: "desktop_windows" },
  { id: "tablet", label: "Tablet", icon: "tablet_mac" },
  { id: "mobile", label: "Mobile", icon: "smartphone" }
];

const DeviceSelector = ({ selectedDevice, onSelectDevice }) => {
  return (
    <div className="position_absolute left_50 translate_-x_50 d_flex gap_3">
      {devices.map(({ id, label, icon }) => {
        const isActive = selectedDevice === id;
        return (
          <div
            key={id}
            className={`cursor_pointer d_flex align_center gap_1 ${
              isActive ? "text_primary font_weight_semibold" : "text_muted"
            }`}
            onClick={() => onSelectDevice(id)}
          >
            <MaterialIcon name={icon} className={`mode-option ${isActive ? "active" : ""}`} />
            <span>{label}</span>
          </div>
        );
      })}
    </div>
  );
};

const AlertForTheMissingFields = ({ missingFields, onSave }) => {
  return (
    <>
      <div className="d_flex align_center py_3 gap_2">
        <div>The following fields are missing from the selected device:</div>
        <Button variant="primary" onClick={onSave}>
          Save Anyway
        </Button>
      </div>
      <>
        <BuilderTabs
          tabs={devices.map(({ id, label }) => ({
            label,
            content: (
              <div>
                {missingFields[id].map(({ type, label, id }) => (
                  <div key={id} className="bg_light shadow_sm border_radius_medium p_2">
                    <div className="d_flex flex_column gap_1">
                      <div className="d_flex align_center gap_1">
                        <div className="icon_wrapper bg_primary_light p_1 border_radius_small">
                          <MaterialIcon name={getFieldIcon(type)} />
                        </div>
                        <span className="font_medium text_dark">{label}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          }))}
        />
      </>
    </>
  );
};

export default PositionFormPlayground;
